/**
 * DG — Injecteur de cartes dans la grille collection (moteur partagé).
 *
 * Sert plusieurs "fournisseurs" (réassurance, bannières promo, …) avec un
 * seul MutationObserver et un seul passage de synchronisation. Chaque
 * fournisseur déclare en HTML, dans un conteneur [data-grid-cards] :
 *   - un <script type="application/json" data-gc-config> :
 *       réassurance : {"slotPrefix":"rc","mode":"interval","first":12,"interval":30}
 *       bannières   : {"slotPrefix":"pb","mode":"fixed","positions":"4, 24, 64, 125"}
 *   - un ou plusieurs <template data-gc-card="<id>"> dont le contenu est le
 *     <li> à injecter.
 *
 * Règles communes :
 *   - Compteur global de produits, cumulatif sur tout l'infinite scroll.
 *     Les cartes injectées ne sont jamais comptées comme des produits.
 *   - Une carte n'est injectée à la position N que s'il existe un produit
 *     APRÈS elle (N < nbProduits). Le thème ajoute les nouveaux produits
 *     après le dernier résultat : garder une carte en milieu de liste
 *     garantit qu'elle ne dérive pas au scroll (cas des positions qui
 *     tombent pile sur une frontière de page, ex. 64 = 16×4).
 *   - Rotation aléatoire par sac mélangé, sans répétition immédiate.
 *   - Idempotence : chaque slot injecté est marqué data-gc-slot ; jamais
 *     ré-inséré, jamais dupliqué.
 *
 * Vanilla JS, aucune dépendance.
 */
(function () {
  'use strict';

  if (window.__dgGridCardsLoaded) return;
  window.__dgGridCardsLoaded = true;

  const DEBOUNCE_MS = 150;

  let providers = [];
  let observer = null;
  let debounceTimer = null;
  let injecting = false;

  function shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /* Tire une carte au hasard dans le sac du fournisseur (re-mélange quand
     il est vide, en évitant de retomber sur la dernière tirée). */
  function drawCard(provider) {
    if (!provider.deck.length) return null;
    if (!provider.bag.length) {
      provider.bag = shuffle(provider.deck.slice());
      if (provider.bag.length > 1 && provider.bag[provider.bag.length - 1].id === provider.lastDrawnId) {
        provider.bag.unshift(provider.bag.pop());
      }
    }
    const card = provider.bag.pop();
    provider.lastDrawnId = card.id;
    return card;
  }

  /* Position produit (1-indexée) pour le slot demandé, ou null si épuisé. */
  function positionForSlot(provider, slot) {
    if (provider.mode === 'fixed') {
      return slot < provider.positions.length ? provider.positions[slot] : null;
    }
    return provider.first + slot * provider.interval;
  }

  function getGrid() {
    const results = document.getElementById('filter-results');
    return results ? results.querySelector('ul[role="list"]') : null;
  }

  /* Les <li> produits : résultats de pagination contenant une product card.
     Exclut les promos natives du thème et toutes nos cartes injectées. */
  function getProductItems(grid) {
    return Array.from(grid.children).filter(
      (li) => li.classList.contains('js-pagination-result') && li.querySelector('product-card')
    );
  }

  function sync() {
    if (!providers.length) return;
    const grid = getGrid();
    if (!grid) return;

    const products = getProductItems(grid);
    const existing = new Set(
      Array.from(grid.querySelectorAll('[data-gc-slot]')).map((el) => el.dataset.gcSlot)
    );

    injecting = true;
    try {
      providers.forEach((provider) => {
        if (!provider.deck.length) return;

        for (let slot = 0; ; slot += 1) {
          const position = positionForSlot(provider, slot);
          if (position == null) break;
          if (position < 1) continue;
          // Stop : pas (encore) de produit après cette position. Les
          // positions sont croissantes → les suivantes le sont aussi.
          if (position >= products.length) break;

          const slotId = provider.slotPrefix + '-' + slot;
          if (existing.has(slotId)) continue;

          const card = drawCard(provider);
          if (!card) break;
          const node = card.template.content.firstElementChild;
          if (!node) continue;

          const li = node.cloneNode(true);
          li.dataset.gcSlot = slotId;
          products[position - 1].insertAdjacentElement('afterend', li);
          existing.add(slotId);
        }
      });
    } finally {
      injecting = false;
    }
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, DEBOUNCE_MS);
  }

  function removeInjected() {
    document.querySelectorAll('#filter-results [data-gc-slot]').forEach((el) => el.remove());
  }

  function buildProvider(container) {
    const cfgEl = container.querySelector('[data-gc-config]');
    if (!cfgEl) return null;

    let cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.warn('[grid-cards] Config invalide', e); // eslint-disable-line
      return null;
    }

    const deck = Array.from(container.querySelectorAll('template[data-gc-card]')).map((t) => ({
      id: t.dataset.gcCard,
      template: t
    }));
    if (!deck.length) return null;

    const provider = {
      slotPrefix: String(cfg.slotPrefix || 'gc'),
      mode: cfg.mode === 'fixed' ? 'fixed' : 'interval',
      deck,
      bag: [],
      lastDrawnId: null
    };

    if (provider.mode === 'fixed') {
      let raw = cfg.positions;
      if (typeof raw === 'string') raw = raw.split(',');
      if (!Array.isArray(raw)) raw = [];
      provider.positions = Array.from(
        new Set(raw.map((n) => parseInt(n, 10)).filter((n) => n >= 1))
      ).sort((a, b) => a - b);
      if (!provider.positions.length) return null;
    } else {
      provider.first = Math.max(1, parseInt(cfg.first, 10) || 12);
      provider.interval = Math.max(1, parseInt(cfg.interval, 10) || 30);
    }

    return provider;
  }

  function setup() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    providers = Array.from(document.querySelectorAll('[data-grid-cards]'))
      .map(buildProvider)
      .filter(Boolean);

    if (!providers.length) {
      // Toutes les sections supprimées / désactivées : nettoyer.
      removeInjected();
      return;
    }

    const pagination = document.querySelector('custom-pagination');
    if (!pagination) return;

    observer = new MutationObserver((mutations) => {
      if (injecting) return;
      if (mutations.some((m) => m.addedNodes.length || m.removedNodes.length)) scheduleSync();
    });
    observer.observe(pagination, { childList: true, subtree: true });

    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // Theme Editor : un ajout/retrait/édition de section re-scanne tous les
  // fournisseurs et réconcilie (les cartes sont re-tirées au hasard).
  document.addEventListener('shopify:section:load', () => {
    removeInjected();
    setup();
  });
  document.addEventListener('shopify:section:unload', () => {
    removeInjected();
    setup();
  });

  // Retour bfcache : ré-attache l'observer ; l'idempotence par slot évite
  // toute duplication des cartes déjà présentes dans le DOM restauré.
  document.addEventListener('on:bfcache:load-restore', setup);
})();
